import { Controller } from "@hotwired/stimulus"
import Logger from '../lib/logger'

/**
 * Presence Indicator Controller
 * 
 * Subscribes to the ActionCable PresenceChannel and updates the UI for users'
 * online/offline status in real-time.
 * 
 * Usage:
 *   <div data-controller="presence-indicator">
 *     <!-- The controller will look for elements with 'data-user-presence-id="X"' -->
 *     <span data-user-presence-id="1" class="presence-dot bg-secondary"></span>
 *   </div>
 */
export default class extends Controller {
    connect() {
        this.onlineUsers = new Set()
        this.setupSubscription()
    }

    disconnect() {
        this.unsubscribe()
    }

    setupSubscription() {
        const cable = this.getActionCable()
        if (!cable) {
            Logger.warn("ActionCable not available for presence indicator")
            return
        }

        this.subscription = cable.subscriptions.create(
            { channel: "PresenceChannel" },
            {
                received: (data) => this.handleReceived(data),
                connected: () => Logger.debug("Presence indicator connected"),
                disconnected: () => Logger.debug("Presence indicator disconnected")
            }
        )
    }

    getActionCable() {
        if (typeof App !== "undefined" && App.cable) {
            return App.cable
        }
        if (typeof window.ActionCable !== "undefined" && window.ActionCable.createConsumer) {
            const url = document.head.querySelector('meta[name="action-cable-url"]')?.content
            return window.ActionCable.createConsumer(url)
        }
        return null
    }

    unsubscribe() {
        if (this.subscription) {
            this.subscription.unsubscribe()
            this.subscription = null
        }
    }

    handleReceived(data) {
        if (!data || !data.user_id) return

        const userId = data.user_id.toString()

        if (data.type === 'user_online' || data.type === 'presence_update') {
            this.onlineUsers.add(userId)
        } else if (data.type === 'user_offline') {
            this.onlineUsers.delete(userId)
        }

        this.updateUserElements(userId)
    }

    updateUserElements(userId) {
        const isOnline = this.onlineUsers.has(userId)

        // Find all elements representing this user
        const elements = document.querySelectorAll(`[data-user-presence-id="${userId}"]`)

        elements.forEach(el => {
            if (isOnline) {
                el.classList.remove('bg-secondary')
                el.classList.add('bg-success')
                el.setAttribute('title', 'Online')
            } else {
                el.classList.remove('bg-success')
                el.classList.add('bg-secondary')
                el.setAttribute('title', 'Offline')
            }
        })
    }
}
